import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Loader2, Mic, FileText, CheckCircle, XCircle, Sparkles,
  Clock, ChevronDown, ChevronUp, Send, AlertTriangle, BarChart3,
  ClipboardCheck, Edit3, Save, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════
// CAPTURE PANEL
// ═══════════════════════════════════════════════════════
function CapturePanel({ patients, onNoteGenerated }) {
  const [selectedPatient, setSelectedPatient] = useState('');
  const [rawInput, setRawInput] = useState('');
  const [noteFormat, setNoteFormat] = useState('soap');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [approving, setApproving] = useState(false);
  const [qualityChecking, setQualityChecking] = useState(false);
  const [qualityResult, setQualityResult] = useState(null);

  const formats = [
    { id: 'soap', label: 'SOAP Note', desc: 'Standard clinical' },
    { id: 'narrative', label: 'Narrative', desc: 'Flowing prose' },
    { id: 'focused', label: 'Focused', desc: 'Quick review' },
  ];

  const sampleObservations = [
    "Visited patient at home. Daughter present. Patient reports pain increased to 6/10 in lower back since yesterday. Taking morphine 15mg q4h but only getting 2 hours of relief. Appears comfortable at rest but grimaces when repositioning. Appetite decreased, only taking sips of broth. Skin intact, no new concerns. Discussed increasing morphine dose with family — they agree. Will call physician for order change. Next visit Thursday.",
    "Patient resting in bed, appears peaceful. Wife reports he slept well last night. Pain 3/10, well-managed on current regimen. Eating small meals, drinking fluids. No new symptoms. Reviewed medications — all current. Family coping well. Chaplain visit scheduled for tomorrow. Continue current plan of care.",
  ];

  const capture = async () => {
    if (!selectedPatient || !rawInput.trim()) return;
    setLoading(true); setResult(null); setQualityResult(null);
    try {
      const res = await fetch(`${API}/api/ambient-doc/capture`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: selectedPatient, raw_input: rawInput, note_format: noteFormat }),
      });
      const data = await res.json();
      setResult(data);
      setEditedText(data.generated_note);
    } catch {}
    setLoading(false);
  };

  const approve = async () => {
    if (!result) return;
    setApproving(true);
    try {
      await fetch(`${API}/api/ambient-doc/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note_id: result.id, edited_text: editing ? editedText : null }),
      });
      setResult(prev => ({ ...prev, status: 'approved' }));
      if (onNoteGenerated) onNoteGenerated();
    } catch {}
    setApproving(false);
  };

  const checkQuality = async () => {
    if (!result) return;
    setQualityChecking(true);
    try {
      const res = await fetch(`${API}/api/ambient-doc/quality-check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note_id: result.id }),
      });
      setQualityResult(await res.json());
    } catch {}
    setQualityChecking(false);
  };

  const visitTypeLabels = {
    routine_visit: 'Routine Visit', symptom_management: 'Symptom Management',
    family_conference: 'Family Conference', admission_assessment: 'Admission',
    recertification: 'Recertification', crisis_visit: 'Crisis Visit',
    medication_review: 'Medication Review', idg_update: 'IDG Update',
    bereavement_follow_up: 'Bereavement',
  };

  return (
    <div data-testid="ambient-capture">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-sage-50 flex items-center justify-center">
          <Mic className="w-5 h-5 text-sage-400" />
        </div>
        <div>
          <h2 className="text-lg font-heading font-semibold text-[#2C3D30]">Ambient Capture</h2>
          <p className="text-xs text-[#5C6A5F]">Paste or dictate visit observations — AI does the rest</p>
        </div>
      </div>

      {/* Patient & Format Selection */}
      <div className="flex gap-2 mb-3">
        <select value={selectedPatient} onChange={e => setSelectedPatient(e.target.value)}
          className="flex-1 rounded-xl border border-sand-200 px-4 py-2.5 text-sm bg-sand-50"
          data-testid="ad-patient-select">
          <option value="">Select patient...</option>
          {patients.map(p => (
            <option key={p.id} value={p.id}>{p.name} — {p.primary_diagnosis || 'N/A'}</option>
          ))}
        </select>
      </div>

      {/* Format pills */}
      <div className="flex gap-2 mb-4">
        {formats.map(f => (
          <button key={f.id} onClick={() => setNoteFormat(f.id)}
            className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 ${
              noteFormat === f.id ? 'bg-sage-400 text-white' : 'bg-sand-200 text-[#5C6A5F] hover:bg-sand-300'
            }`} data-testid={`ad-format-${f.id}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Raw input */}
      <textarea value={rawInput} onChange={e => setRawInput(e.target.value)}
        placeholder="Paste your visit observations here... describe what you saw, heard, and did during the visit."
        className="w-full rounded-xl border border-sand-200 px-4 py-3 text-sm min-h-[140px] resize-none bg-sand-50 focus:border-sage-400 mb-2"
        data-testid="ad-raw-input" />

      {/* Sample buttons */}
      {!rawInput && (
        <div className="flex gap-2 mb-3">
          {sampleObservations.map((s, i) => (
            <button key={i} onClick={() => setRawInput(s)}
              className="text-xs px-3 py-1.5 rounded-full bg-sand-200 text-[#5C6A5F] hover:bg-sky-50 hover:text-sky-500 transition-colors"
              data-testid={`ad-sample-${i}`}>
              Sample {i + 1}
            </button>
          ))}
        </div>
      )}

      <Button onClick={capture} disabled={loading || !selectedPatient || !rawInput.trim()}
        className="w-full rounded-full bg-sage-400 hover:bg-sage-500 text-white mb-4"
        data-testid="ad-capture-btn">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Generating clinical note...</>
          : <><Sparkles className="w-4 h-4 mr-2" />Generate Note</>}
      </Button>

      {/* Generated Result */}
      {result && (
        <div className="space-y-4" data-testid="ad-result">
          {/* Visit Type Detection */}
          <div className="flex items-center gap-2">
            <Badge className="bg-sky-50 text-sky-500 rounded-full text-xs">
              {visitTypeLabels[result.visit_type] || result.visit_type}
            </Badge>
            <Badge variant="outline" className="rounded-full text-xs border-sand-200">
              {result.note_format.toUpperCase()}
            </Badge>
            <Badge className={`rounded-full text-xs ${result.status === 'approved' ? 'bg-sage-50 text-sage-400' : 'bg-terra-50 text-terra-400'}`}>
              {result.status === 'approved' ? 'Approved' : 'Draft'}
            </Badge>
          </div>

          {/* Generated Note */}
          <div className="p-5 rounded-2xl bg-sand-50 border border-sand-200 border-l-4 border-l-sage-400" data-testid="ad-generated-note">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-heading font-semibold text-[#2C3D30]">{result.patient_name}</span>
              <button onClick={() => setEditing(!editing)}
                className="text-xs flex items-center gap-1 text-[#5C6A5F] hover:text-sage-400"
                data-testid="ad-edit-toggle">
                <Edit3 className="w-3 h-3" /> {editing ? 'Preview' : 'Edit'}
              </button>
            </div>
            {editing ? (
              <textarea value={editedText} onChange={e => setEditedText(e.target.value)}
                className="w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm min-h-[200px] resize-none bg-white"
                data-testid="ad-edit-textarea" />
            ) : (
              <p className="text-sm text-[#2C3D30] leading-relaxed whitespace-pre-wrap">
                {editing ? editedText : result.generated_note}
              </p>
            )}
          </div>

          {/* Extracted Data */}
          {result.extracted_data && Object.keys(result.extracted_data).length > 0 && (
            <div className="p-4 rounded-2xl bg-sky-50 border border-sky-100" data-testid="ad-extracted-data">
              <p className="text-xs font-medium text-sky-500 mb-2 tracking-wide uppercase">Auto-Extracted Clinical Data</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(result.extracted_data).filter(([, v]) => v && v !== 'not mentioned' && v !== 'none' && v !== 'stable' && v !== 'N/A').map(([key, value]) => (
                  <div key={key} className="text-xs">
                    <span className="text-[#5C6A5F] capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                    <span className="text-[#2C3D30] font-medium">{value}</span>
                  </div>
                ))}
              </div>
              {result.extracted_data.escalation_needed?.toLowerCase() === 'yes' && (
                <div className="mt-2 p-2 rounded-xl bg-terra-50 border border-terra-200 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-terra-400" />
                  <span className="text-xs text-terra-400 font-medium">Escalation needed: {result.extracted_data.escalation_reason}</span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            {result.status !== 'approved' && (
              <Button onClick={approve} disabled={approving} className="flex-1 rounded-full bg-sage-400 hover:bg-sage-500"
                data-testid="ad-approve-btn">
                {approving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                Approve & Save to Chart
              </Button>
            )}
            <Button onClick={checkQuality} disabled={qualityChecking} variant="outline"
              className="flex-1 rounded-full border-sky-200 text-sky-500 hover:bg-sky-50"
              data-testid="ad-quality-btn">
              {qualityChecking ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ClipboardCheck className="w-4 h-4 mr-1" />}
              Quality Check
            </Button>
          </div>

          {/* Quality Result */}
          {qualityResult && (
            <div className="p-5 rounded-2xl bg-sand-50 border border-sand-200" data-testid="ad-quality-result">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-heading font-semibold text-[#2C3D30]">Documentation Quality</span>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-heading font-bold text-[#2C3D30]">{qualityResult.score}/100</span>
                  <Badge className={`rounded-full text-xs ${
                    qualityResult.cms_readiness === 'Ready' ? 'bg-sage-50 text-sage-400'
                    : qualityResult.cms_readiness === 'Needs Revision' ? 'bg-terra-50 text-terra-400'
                    : 'bg-terra-100 text-terra-500'
                  }`}>
                    {qualityResult.cms_readiness}
                  </Badge>
                </div>
              </div>
              <p className="text-sm text-[#5C6A5F] leading-relaxed whitespace-pre-wrap">{qualityResult.full_report}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// STATS & HISTORY
// ═══════════════════════════════════════════════════════
function StatsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/ambient-doc/stats`)
      .then(r => r.json()).then(setStats)
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-sage-400" /></div>;

  return (
    <div data-testid="ambient-stats">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-sky-300" />
        </div>
        <div>
          <h2 className="text-lg font-heading font-semibold text-[#2C3D30]">Documentation Stats</h2>
          <p className="text-xs text-[#5C6A5F]">Quality trends and usage metrics</p>
        </div>
      </div>

      {stats && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-sage-50 rounded-2xl p-4 border border-sage-100" data-testid="stats-total">
              <p className="text-xs text-sage-600 font-medium">Total Notes</p>
              <p className="text-2xl font-heading font-bold text-[#2C3D30]">{stats.total_notes}</p>
            </div>
            <div className="bg-sky-50 rounded-2xl p-4 border border-sky-100" data-testid="stats-approved">
              <p className="text-xs text-sky-500 font-medium">Approved</p>
              <p className="text-2xl font-heading font-bold text-[#2C3D30]">{stats.approved}</p>
            </div>
            <div className="bg-terra-50 rounded-2xl p-4 border border-terra-100" data-testid="stats-quality">
              <p className="text-xs text-terra-400 font-medium">Avg Quality</p>
              <p className="text-2xl font-heading font-bold text-[#2C3D30]">{stats.avg_quality_score || '—'}</p>
            </div>
            <div className="bg-sand-200 rounded-2xl p-4 border border-sand-300" data-testid="stats-edited">
              <p className="text-xs text-[#5C6A5F] font-medium">Edit Rate</p>
              <p className="text-2xl font-heading font-bold text-[#2C3D30]">{stats.edited_rate}%</p>
            </div>
          </div>

          {Object.keys(stats.format_distribution || {}).length > 0 && (
            <div className="p-4 rounded-2xl bg-sand-50 border border-sand-200">
              <p className="text-xs text-[#5C6A5F] font-medium mb-2">Format Distribution</p>
              <div className="flex gap-3">
                {Object.entries(stats.format_distribution).map(([fmt, count]) => (
                  <div key={fmt} className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-[#2C3D30] capitalize">{fmt}</span>
                    <Badge variant="outline" className="text-xs rounded-full border-sand-200">{count}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════
export default function AmbientDocumentationPage() {
  const [patients, setPatients] = useState([]);
  const [activeTab, setActiveTab] = useState('capture');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetch(`${API}/api/ambient-doc/patients-list`)
      .then(r => r.ok ? r.json() : { patients: [] })
      .then(d => setPatients(d.patients || []))
      .catch(() => {});
  }, []);

  const tabs = [
    { id: 'capture', label: 'Capture & Generate', icon: Mic, color: 'sage' },
    { id: 'stats', label: 'Quality Dashboard', icon: BarChart3, color: 'sky' },
  ];

  const tabColors = {
    sage: 'bg-sage-50 text-sage-400 border border-sage-200',
    sky: 'bg-sky-50 text-sky-500 border border-sky-200',
  };

  return (
    <div className="min-h-screen bg-sand-100" data-testid="ambient-doc-page">
      <div className="max-w-4xl mx-auto p-6 sm:p-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#2C3D30] tracking-tight">Ambient Documentation</h1>
            <Badge className="bg-sage-50 text-sage-400 rounded-full text-xs">AI-Powered</Badge>
          </div>
          <p className="text-sm text-[#5C6A5F]">Observe. Capture. Done. — Clinical notes that write themselves.</p>
        </div>

        <div className="flex gap-2 mb-8 pb-4 border-b border-sand-200">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                activeTab === tab.id ? tabColors[tab.color] : 'text-[#5C6A5F] hover:text-[#2C3D30] hover:bg-sand-200'
              }`} data-testid={`ad-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-sand-50 rounded-2xl border border-sand-200 p-6 sm:p-8">
          {activeTab === 'capture' && <CapturePanel patients={patients} onNoteGenerated={() => setRefreshKey(k => k + 1)} />}
          {activeTab === 'stats' && <StatsPanel key={refreshKey} />}
        </div>
      </div>
    </div>
  );
}
